import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Container,
  useTheme,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AddIcon from "@mui/icons-material/Add";
import PublishIcon from "@mui/icons-material/Publish";
import CodeIcon from "@mui/icons-material/Code";
import AnimatedDocIcon from "./AnimatedDocIcon";
import ImportDialog from "./ImportDialog";

const Section = ({ title, icon: Icon, count, children, emptyMessage }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      mb: 2,
      flex: 1,
      minHeight: 100,
      display: "flex",
      flexDirection: "column",
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      {Icon && <Icon sx={{ fontSize: 20, color: "text.secondary" }} />}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
      {count != null && count > 0 && (
        <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
          ({count})
        </Typography>
      )}
    </Box>
    <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
      {children == null ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
          {emptyMessage}
        </Typography>
      ) : (
        children
      )}
    </Box>
  </Paper>
);

const FormRow = ({ form, isAnnotationComplete, isJsonComplete, onClick, disabled }) => {
  const hasAnyComplete = isAnnotationComplete || isJsonComplete;
  return (
  <ListItemButton
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    sx={{
      py: 1.25,
      px: 2,
      mb: 0.5,
      borderRadius: 1.5,
      borderLeft: hasAnyComplete ? 4 : 0,
      borderLeftColor: hasAnyComplete ? (isJsonComplete ? "success.main" : "warning.main") : "transparent",
      bgcolor: hasAnyComplete ? "action.selected" : undefined,
      "&:hover:not(.Mui-disabled)": {
        bgcolor: "action.hover",
      },
    }}
  >
    <ListItemText
      primary={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body1" noWrap sx={{ flex: 1, fontWeight: 500 }}>
            {form.name || form.shortName || form.formName || "Untitled"}
          </Typography>
          {isAnnotationComplete && (
            <CheckCircleIcon
              sx={{ color: "warning.main", fontSize: 20 }}
              titleAccess="Annotation complete"
            />
          )}
          {isJsonComplete && (
            <CheckCircleIcon
              sx={{ color: "success.main", fontSize: 20 }}
              titleAccess="JSON complete"
            />
          )}
        </Box>
      }
      secondary={
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
          {[form.shortName, form.locale, form.version && `v${form.version}`].filter(Boolean).join(" · ")}
        </Typography>
      }
    />
  </ListItemButton>
  );
};

const DashboardPage = ({
  studioForms,
  studioFormsLoading,
  annotationCompleteMap,
  jsonCompleteMap = {},
  activeProfileId,
  activeRole,
  onOpenForm,
  onPdfUpload,
  onFormImport,
}) => {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const pdfInputRef = useRef(null);

  const assignedToMe = (studioForms || []).filter((f) => f.assignedTo === activeProfileId);
  const completedAnnotations = (studioForms || []).filter((f) => !!annotationCompleteMap[f.id]);
  const completedJson = (studioForms || []).filter((f) => !!jsonCompleteMap[f.id]);
  const showCompletedSection = activeRole === "admin" || activeRole === "json";

  const handleFileSelection = (file) => {
    if (!file) return;
    setIsLoading(true);
    setTimeout(() => setIsExpanding(true), 300);
    setTimeout(() => {
      if (file.type === "application/pdf") {
        onPdfUpload(file);
      } else {
        alert("Please upload a PDF file.");
        setIsLoading(false);
        setIsExpanding(false);
      }
    }, 800);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files?.length) handleFileSelection(files[0]);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        py: 3,
      }}
    >
      {/* Branding */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3, gap: 1 }}>
        <img src="https://storage.googleapis.com/mevi-assets/mevi-logo.svg" alt="Mevi Logo" style={{ height: "48px" }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background:
              theme.palette.mode === "light"
                ? "linear-gradient(45deg, #2c3e50 30%, #3498db 90%)"
                : "linear-gradient(45deg, #f4f4f5 30%, #2dd4bf 90%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Mevi Form Studio
        </Typography>
      </Box>

      {/* Main: lists left, new form right */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left: Your work */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            Your work
          </Typography>

          {studioFormsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Section
                title="Assigned to me"
                icon={AssignmentIcon}
                count={assignedToMe.length}
                emptyMessage="No forms assigned to you."
              >
                {assignedToMe.length > 0 ? (
                  <List dense disablePadding sx={{ mt: -0.5 }}>
                    {assignedToMe.map((form) => (
                      <FormRow
                        key={form.id}
                        form={form}
                        isAnnotationComplete={!!annotationCompleteMap[form.id]}
                        isJsonComplete={!!jsonCompleteMap[form.id]}
                        onClick={() => onOpenForm(form)}
                      />
                    ))}
                  </List>
                ) : null}
              </Section>

              {showCompletedSection && (
                <Section
                  title="Completed annotations"
                  icon={DoneAllIcon}
                  count={completedAnnotations.length}
                  emptyMessage="No annotator-complete forms yet."
                >
                  {completedAnnotations.length > 0 ? (
                    <List dense disablePadding sx={{ mt: -0.5 }}>
                      {completedAnnotations.map((form) => (
                        <FormRow
                          key={form.id}
                          form={form}
                          isAnnotationComplete={!!annotationCompleteMap[form.id]}
                          isJsonComplete={!!jsonCompleteMap[form.id]}
                          onClick={() => onOpenForm(form)}
                        />
                      ))}
                    </List>
                  ) : null}
                </Section>
              )}

              {showCompletedSection && (
                <Section
                  title="JSON complete"
                  icon={CodeIcon}
                  count={completedJson.length}
                  emptyMessage="No JSON-complete forms yet."
                >
                  {completedJson.length > 0 ? (
                    <List dense disablePadding sx={{ mt: -0.5 }}>
                      {completedJson.map((form) => (
                        <FormRow
                          key={form.id}
                          form={form}
                          isAnnotationComplete={!!annotationCompleteMap[form.id]}
                          isJsonComplete={!!jsonCompleteMap[form.id]}
                          onClick={() => onOpenForm(form)}
                        />
                      ))}
                    </List>
                  ) : null}
                </Section>
              )}
            </>
          )}
        </Box>

        {/* Right: New form */}
        <Box sx={{ width: { xs: "100%", md: 360 }, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <AddIcon sx={{ fontSize: 22, color: "text.secondary" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              New form
            </Typography>
          </Box>
          <Paper
            elevation={0}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={handleDrop}
            onClick={() => pdfInputRef.current?.click()}
            sx={{
              p: 3,
              borderRadius: 2,
              border: "2px dashed",
              borderColor: isDragging ? "primary.main" : "divider",
              bgcolor: isDragging ? "action.hover" : "background.paper",
              cursor: "pointer",
              textAlign: "center",
              transition: "all 0.2s ease",
              "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
            }}
          >
            <Box sx={{ mb: 2 }}>
              <AnimatedDocIcon animate={isLoading || isDragging} size={1.2} />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
              {isLoading ? "Processing…" : isDragging ? "Release to upload" : "Drop PDF here"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              or click to browse
            </Typography>
            <input
              type="file"
              accept="application/pdf"
              ref={pdfInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files?.length) handleFileSelection(e.target.files[0]);
              }}
            />
          </Paper>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PublishIcon />}
              onClick={() => pdfInputRef.current?.click()}
              fullWidth
              sx={{ justifyContent: "flex-start" }}
            >
              Upload PDF
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CodeIcon />}
              onClick={() => setImportDialogOpen(true)}
              fullWidth
              sx={{ justifyContent: "flex-start" }}
            >
              Import JSON
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Loading overlay for PDF/import */}
      {isExpanding && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "background.default",
            backdropFilter: "blur(8px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AnimatedDocIcon animate size={2} />
        </Box>
      )}

      <ImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={(formData, rawSource) => {
          setIsLoading(true);
          setIsExpanding(true);
          setTimeout(() => onFormImport(formData, rawSource), 800);
        }}
      />
    </Container>
  );
};

export default DashboardPage;
